import { Pill } from 'lucide-react';
import type { Patient } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';
import './PrescriptionsSummary.scss';

interface PrescriptionsSummaryProps {
  patient: Patient;
}

/**
 * Format a prescription date using the dashboard display convention.
 *
 * @param value - Date string from the extraction
 * @returns A locale date string, the raw value, or an em dash
 */
function formatDate(value?: string): string {
  return formatDisplayDate(value, '—');
}

/**
 * Prescriptions summary. Renders the medication list extracted from the patient's
 * prescription documents (one drug per document). Falls back to the raw narrative
 * when no structured prescription data is present.
 *
 * @param patient - The patient hydrated with `prescriptions` from the document summary
 */
export function PrescriptionsSummary({ patient }: PrescriptionsSummaryProps) {
  const prescriptions = patient.prescriptions;
  const narrative = patient.narrative;

  if (!prescriptions || prescriptions.medications.length === 0) {
    return (
      <div className="prescriptions-report">
        <p style={{ color: 'var(--color-text-muted)' }}>
          {narrative || 'No prescription data available for this patient.'}
        </p>
      </div>
    );
  }

  return (
    <div className="prescriptions-report">
      {/* Header */}
      <div className="prescriptions-header">
        <div className="prescriptions-title">
          <Pill className="prescriptions-icon" />
          <h2 className="prescriptions-title-text">Prescription Orders</h2>
          <span className="prescriptions-subtitle">Date: {formatDate(prescriptions.documentDate)}</span>
        </div>
      </div>

      {/* Prescription Info */}
      <div className="prescriptions-info">
        <div className="prescriptions-grid">
          <div>
            <p className="prescriptions-label">Prescribing Physician</p>
            <p className="prescriptions-value">{prescriptions.prescribingPhysician || 'N/A'}</p>
          </div>
          <div>
            <p className="prescriptions-label">Patient Name</p>
            <p className="prescriptions-value">{patient.name}</p>
          </div>
          <div>
            <p className="prescriptions-label">Total Medications</p>
            <p className="prescriptions-value">{prescriptions.medications.length}</p>
          </div>
        </div>
      </div>

      {/* Reference medication table */}
      <div className="prescriptions-section">
        <h3 className="prescriptions-section-title">
          Medications
          <span className="prescriptions-section-subtitle">({prescriptions.medications.length} prescriptions)</span>
        </h3>

        <div className="prescriptions-table-wrap">
          <table className="prescriptions-table">
            <thead>
              <tr>
                <th>Medication</th>
                <th>Instructions</th>
                <th>Generic</th>
                <th>Disp / Refills</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {prescriptions.medications.map((medication, index) => (
                <tr key={`${medication.name}-${index}`}>
                  <td className="prescription-medication-name">{medication.name || '—'}</td>
                  <td>{medication.instructions || '—'}</td>
                  <td className="prescription-generic">{medication.generic || '—'}</td>
                  <td>
                    <span className="prescription-quantity">
                      {[medication.quantity, medication.quantityText].filter(Boolean).join(' ') || '—'}
                    </span>
                    <span className="prescription-refills">
                      {medication.refills ? `${medication.refills} refills` : 'No refills reported'}
                    </span>
                  </td>
                  <td>
                    {medication.status ? (
                      <span className={`prescription-status prescription-status--${medication.status.toLowerCase()}`}>
                        {medication.status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
